// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Stack, TextField} from '@deere/isg.component-library';
import ValidationInput from 'Ui/components/common/form/validation-input';
import ValidationMultiSelect from 'Ui/components/common/form/validation-multi-select';
import {getHoleOptions, validateDate, getLocalTimeFormats, getValidatedTime} from 'Ui/components/manual-data/manual-data-utils';
import {MANUAL_DATA_TIME_FORMAT} from 'Utils/time-utils';
import {capitalizeFirstLetter, replaceTranslationNames} from 'Utils/translation-utils';
import {getPreferredDataTypeForm} from 'Utils/unit-conversion-utils';
import {MIN_NUMERIC_VALUE} from 'Ui/constants/common-constants';
import {CLIPPING_YIELD, CLIPPING_YIELD_VOLUME, CLIPPING_YIELD_WEIGHT} from 'Common/constants/data-group-constants';
import {getUOMOverrideUnits} from 'Common/constants/uom-override-units';
import {getDataType} from 'Common/utils/unit-of-measure-utils';
import DateTimePicker from 'Ui/components/common/date-picker/date-time-picker';

function ClippingYieldEntryForm(props) {
    const {
        membership,
        setValid,
        setValues,
        translations,
        values
    } = props;

    const [validatedDate, setValidatedDate] = React.useState();
    const [validatedTime, setValidatedTime] = React.useState();

    const {
        clippingYieldUOM,
        dataType,
        holesOptions,
        minimumValueMessage,
        overrideUnits
    } = React.useMemo(() => {
        const holesOptions = getHoleOptions(translations);
        const overrideUnits = getUOMOverrideUnits(translations);

        const clippingYieldUOM = values.manualData ? values.manualData[0].data_unit :
            getPreferredDataTypeForm(membership.preferredDataTypes, membership.unitOfMeasure, [CLIPPING_YIELD, CLIPPING_YIELD_VOLUME, CLIPPING_YIELD_WEIGHT]);

        const minimumValueMessage = replaceTranslationNames(translations.VALUE_GREATER_THAN_OR_EQUAL, {
            '0': MIN_NUMERIC_VALUE
        });

        const dataType = getDataType(CLIPPING_YIELD, clippingYieldUOM);

        return {
            clippingYieldUOM,
            dataType,
            holesOptions,
            minimumValueMessage,
            overrideUnits
        };
    }, [membership]);

    const uomValue = React.useMemo(() => {
        if (!values.manualData) {
            return '';
        }

        return values.manualData[0].value;
    }, [values.manualData]);

    React.useEffect(() => {
        const validated = validateDate(values.timeSample, setValid);

        setValidatedDate(validated);
        setValidatedTime(getValidatedTime(validated?.toDate()));
    }, [values.timeSample]);

    function onValuesChange(name, value) {
        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    }

    return (
        <div className='settings-body manual-data-form'>
            <DateTimePicker
                dateProps={{
                    validatedDate
                }}
                onChange={(date) => onValuesChange('timeSample', date?.format(MANUAL_DATA_TIME_FORMAT))}
                timeProps={{
                    validatedTime,
                    timeFormat: getLocalTimeFormats(),
                    timeIntervals: 1,
                    enableVirtualization: true
                }}
                translations
            />

            <Stack
                direction='row'
                spacing={4}
                sx={{
                    width: '100%',
                    '& > *': {
                        flex: 1,
                        minWidth: 0
                    }
                }}
            >
                <ValidationMultiSelect
                    className='mobile-required-multiselect'
                    disabled={false}
                    hideCheckboxes={true}
                    items={holesOptions}
                    label={translations.ONLINK_HOLES}
                    name='hole'
                    onChange={(event) => onValuesChange('hole', event[0])}
                    required={true}
                    selectedIds={[values.hole]}
                    setValid={setValid}
                    single
                    tabIndex={0}
                />
                <ValidationInput
                    errors={{
                        rangeUnderflow: minimumValueMessage
                    }}
                    fullWidth={true}
                    label={`${translations.YIELD} (${capitalizeFirstLetter(overrideUnits[clippingYieldUOM].title)})`}
                    min={0}
                    name={clippingYieldUOM}
                    onChange={(event) => {
                        const value = event.target.value === '' ? '' : parseFloat(event.target.value);

                        onValuesChange('manualData', [{
                            'data_type': dataType,
                            'data_unit': clippingYieldUOM,
                            value
                        }]);
                    }}
                    required={true}
                    setValid={setValid}
                    tabIndex={0}
                    type='number'
                    value={uomValue}
                />
            </Stack>
            <TextField
                label={translations.NOTES}
                multiline
                name='note'
                onChange={(event) => onValuesChange('note', event.target.value)}
                tabIndex={0}
                value={values.note || ''}
            />
        </div>
    );
}

ClippingYieldEntryForm.propTypes = {
    membership: PropTypes.membership,
    setValid: PropTypes.func,
    setValues: PropTypes.func,
    translations: PropTypes.translations,
    values: PropTypes.object
};

export default ClippingYieldEntryForm;
